import { useCallback, useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { cn } from '@workspace/ui/lib/utils';
import figmaWarmGrainBackground from './assets/bg-figma-warm-grain.png';
import { NOTIFICATION_BANNER_VARIANT } from './types';
import type { NotificationBannerCarouselItem, NotificationBannerProps } from './types';
import { formatPromoCountdown, usePromoCountdown } from '../promo-countdown';

const STANDARD_BANNER_CTA_CLASS =
    'flex h-5 shrink-0 cursor-pointer items-center justify-center rounded-full bg-brand-accent-orange px-2 text-white';
const PROMOTION_BANNER_CTA_CLASS =
    'flex h-5 shrink-0 cursor-pointer items-center justify-center rounded-full bg-brand-purple px-2 text-promo-banner-cta-foreground';
const PROMOTION_COUNTDOWN_BANNER_CTA_CLASS =
    'flex h-[22px] shrink-0 cursor-pointer items-center justify-center rounded-[6px] bg-brand-orange px-2 py-px text-promo-banner-cta-foreground shadow-[var(--shadow-promo-banner-countdown)] hover:opacity-90';

function resolveImageSource(value: unknown): string {
    if (typeof value === 'string') {
        return value;
    }

    if (
        typeof value === 'object' &&
        value !== null &&
        'src' in value &&
        typeof value.src === 'string'
    ) {
        return value.src;
    }

    throw new TypeError('Notification banner background image did not resolve to a URL');
}

const FIGMA_WARM_GRAIN_BACKGROUND_SRC = resolveImageSource(figmaWarmGrainBackground);

const ANIMATION_STYLES = `
@keyframes banner-push-next-incoming {
    0% {
        opacity: 0.96;
        transform: translate3d(0, 100%, 0);
    }
    100% {
        opacity: 1;
        transform: translate3d(0, 0, 0);
    }
}

@keyframes banner-push-next-outgoing {
    0% {
        opacity: 1;
        transform: translate3d(0, 0, 0);
    }
    100% {
        opacity: 0.92;
        transform: translate3d(0, -100%, 0);
    }
}

@media (prefers-reduced-motion: reduce) {
    [data-banner-switch-motion] {
        animation: none !important;
        transform: none !important;
    }
}
`;

const DEFAULT_CAROUSEL_INTERVAL_MS = 3000;
const BANNER_PUSH_TRANSITION_MS = 300;
const BANNER_PUSH_EASING = 'cubic-bezier(0.16, 1, 0.3, 1)';
type BannerTransitionLayer = 'incoming' | 'outgoing';

interface BannerCarouselState {
    activeIndex: number;
    previousIndex: number | null;
    transitionId: number;
}

function BannerAnimationStyles() {
    return <style>{ANIMATION_STYLES}</style>;
}

function BannerBackground() {
    return (
        <img
            src={FIGMA_WARM_GRAIN_BACKGROUND_SRC}
            alt=""
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 size-full object-fill"
            data-testid="notification-banner-background-current"
            data-variant={NOTIFICATION_BANNER_VARIANT}
        />
    );
}

function BannerForegroundNoiseFilter() {
    return (
        <svg aria-hidden="true" className="pointer-events-none absolute size-0">
            <defs>
                <filter id="notification-banner-foreground-noise" colorInterpolationFilters="sRGB">
                    <feTurbulence
                        type="fractalNoise"
                        baseFrequency="1.25 1.25"
                        numOctaves="4"
                        seed="1"
                        stitchTiles="stitch"
                        result="noise"
                    />
                    <feColorMatrix
                        in="noise"
                        type="matrix"
                        values="0 0 0 0 0
                                0 0 0 0 0
                                0 0 0 0 0
                                0.2126 0.7152 0.0722 0 0"
                        result="noise-alpha"
                    />
                    <feComponentTransfer in="noise-alpha" result="mono-noise-alpha">
                        <feFuncA type="discrete" tableValues="0 1" />
                    </feComponentTransfer>
                    <feFlood floodColor="white" floodOpacity="0.3" result="noise-color-base" />
                    <feColorMatrix
                        in="noise-color-base"
                        type="matrix"
                        values="1 0 0 0 0
                                0 0.9568627451 0 0 0
                                0 0 0.7490196078 0 0
                                0 0 0 1 0"
                        result="noise-color"
                    />
                    <feComposite
                        in="noise-color"
                        in2="mono-noise-alpha"
                        operator="in"
                        result="colored-noise"
                    />
                    <feComposite
                        in="colored-noise"
                        in2="SourceAlpha"
                        operator="in"
                        result="foreground-noise"
                    />
                    <feMerge>
                        <feMergeNode in="SourceGraphic" />
                        <feMergeNode in="foreground-noise" />
                    </feMerge>
                </filter>
            </defs>
        </svg>
    );
}

function BannerCountdownSeparator() {
    return (
        <span
            aria-hidden="true"
            className="grid h-[22px] w-[13px] shrink-0 place-items-center text-promo-banner-message-foreground"
            data-testid="notification-banner-countdown-separator"
        >
            <svg
                aria-hidden="true"
                className="size-full"
                data-testid="notification-banner-countdown-separator-mark"
                viewBox="0 0 13 22"
            >
                <circle cx="6.5" cy="8.5" fill="currentColor" r="1" />
                <circle cx="6.5" cy="13.5" fill="currentColor" r="1" />
            </svg>
        </span>
    );
}

function BannerCountdown({ formatted }: { formatted: string }) {
    const groups = formatted.split(':').map((group) => group.padStart(2, '0').slice(-2));
    const displayValue = groups.join(':');

    return (
        <span
            aria-label={displayValue}
            className="inline-grid h-[22px] w-[101px] shrink-0 grid-cols-[25px_13px_25px_13px_25px] items-center font-sans"
            data-testid="notification-banner-countdown"
            role="timer"
        >
            {groups.map((group, groupIndex) => (
                <span aria-hidden="true" className="contents" key={`${groupIndex}-${group.length}`}>
                    {groupIndex > 0 ? <BannerCountdownSeparator /> : null}
                    <span
                        className="inline-flex h-5 w-[25px] shrink-0 items-center justify-center rounded-[6px] border border-brand-orange/50 bg-white px-[5px] shadow-xs"
                        data-testid="notification-banner-countdown-group"
                    >
                        <span
                            className="block shrink-0 text-center font-sans text-xs font-bold leading-5 text-brand-orange"
                            data-testid="notification-banner-countdown-digit"
                        >
                            {group}
                        </span>
                    </span>
                </span>
            ))}
        </span>
    );
}

function useBannerCarouselIndex(itemCount: number, intervalMs: number, isAutoPlayPaused: boolean) {
    const [state, setState] = useState<BannerCarouselState>({
        activeIndex: 0,
        previousIndex: null,
        transitionId: 0,
    });

    useEffect(() => {
        setState({
            activeIndex: 0,
            previousIndex: null,
            transitionId: 0,
        });
    }, [itemCount]);

    const showNext = useCallback(() => {
        if (itemCount <= 1) return;

        setState((current) => {
            const nextIndex = (current.activeIndex + 1) % itemCount;

            if (nextIndex === current.activeIndex) return current;

            return {
                activeIndex: nextIndex,
                previousIndex: current.activeIndex,
                transitionId: current.transitionId + 1,
            };
        });
    }, [itemCount]);

    useEffect(() => {
        if (itemCount <= 1 || isAutoPlayPaused) return;

        const timer = window.setInterval(showNext, intervalMs);

        return () => window.clearInterval(timer);
    }, [intervalMs, isAutoPlayPaused, itemCount, showNext]);

    useEffect(() => {
        if (state.previousIndex === null) return;

        const timer = window.setTimeout(() => {
            setState((current) => {
                if (current.transitionId !== state.transitionId) return current;
                return { ...current, previousIndex: null };
            });
        }, BANNER_PUSH_TRANSITION_MS);

        return () => window.clearTimeout(timer);
    }, [state.previousIndex, state.transitionId]);

    return {
        activeIndex: state.activeIndex,
        previousIndex: state.previousIndex,
        transitionId: state.transitionId,
    };
}

function getBannerPushAnimation(
    layer: BannerTransitionLayer,
    shouldAnimate: boolean
): React.CSSProperties | undefined {
    if (!shouldAnimate) return undefined;

    return {
        animation: `banner-push-next-${layer} ${BANNER_PUSH_TRANSITION_MS}ms ${BANNER_PUSH_EASING} both`,
        willChange: 'transform, opacity',
    };
}

function BannerContentPage({
    banner,
    onCtaClick,
    className,
    style,
    isInteractive,
    testId,
    motionLayer,
}: {
    banner: NotificationBannerProps;
    onCtaClick: NotificationBannerProps['onCtaClick'];
    className?: string;
    style?: React.CSSProperties;
    isInteractive: boolean;
    testId: string;
    motionLayer?: BannerTransitionLayer;
}) {
    const ctaText = banner.ctaText ?? 'Read more';
    const showCta = banner.showCta ?? false;
    const countdownParts = usePromoCountdown(banner.countdownEndTime);
    const showCountdown = countdownParts !== null;
    const isPromotion = banner.appearance === 'promotion';
    const messageContent = (
        <span
            className={cn(
                'truncate font-medium',
                isPromotion
                    ? 'min-w-0 text-sm text-promo-banner-message-foreground'
                    : 'text-[13px] text-brand-orange',
                'leading-normal'
            )}
            data-testid={isInteractive ? 'notification-banner-message' : undefined}
        >
            {banner.message}
        </span>
    );

    return (
        <div
            className={cn(
                'flex h-full min-h-px min-w-px flex-1 items-center justify-center',
                showCountdown ? 'gap-2.5' : 'gap-3',
                className
            )}
            aria-hidden={!isInteractive}
            data-banner-switch-motion={motionLayer}
            data-testid={testId}
            style={style}
        >
            {showCountdown ? (
                <BannerCountdown formatted={formatPromoCountdown(countdownParts)} />
            ) : null}
            {messageContent}

            {showCta && onCtaClick && (
                <button
                    type="button"
                    onClick={isInteractive ? () => onCtaClick(banner) : undefined}
                    tabIndex={isInteractive ? undefined : -1}
                    className={
                        isPromotion
                            ? showCountdown
                                ? PROMOTION_COUNTDOWN_BANNER_CTA_CLASS
                                : PROMOTION_BANNER_CTA_CLASS
                            : STANDARD_BANNER_CTA_CLASS
                    }
                    data-testid={isInteractive ? 'notification-banner-cta' : undefined}
                >
                    <span
                        className={cn(
                            'whitespace-nowrap font-sans font-semibold',
                            isPromotion
                                ? showCountdown
                                    ? 'text-xs leading-normal'
                                    : 'text-[13px] leading-normal'
                                : 'text-xs leading-6'
                        )}
                    >
                        {ctaText}
                    </span>
                </button>
            )}
        </div>
    );
}

function BannerContent({
    activeBanner,
    previousBanner,
    hasCountdown,
    showCloseButton,
    onClose,
    carouselItemCount,
    transitionId,
}: {
    activeBanner: NotificationBannerProps;
    previousBanner?: NotificationBannerProps;
    hasCountdown: boolean;
    showCloseButton: boolean;
    onClose: () => void;
    carouselItemCount: number;
    transitionId: number;
}) {
    const isCarousel = carouselItemCount > 1;
    const shouldAnimate = isCarousel && Boolean(previousBanner);
    const isPromotion = activeBanner.appearance === 'promotion';

    return (
        <div
            className={cn(
                'relative z-10 flex h-full min-h-px min-w-px flex-1 items-center justify-center gap-3',
                isPromotion && '[filter:url(#notification-banner-foreground-noise)]'
            )}
        >
            <div
                className={cn(
                    'relative min-h-px min-w-px flex-1 overflow-hidden',
                    hasCountdown ? 'h-6' : 'h-full'
                )}
                data-testid="notification-banner-content"
            >
                {previousBanner && (
                    <BannerContentPage
                        key={`previous-${transitionId}-${previousBanner.id}`}
                        banner={previousBanner}
                        onCtaClick={activeBanner.onCtaClick}
                        className="pointer-events-none absolute inset-0"
                        style={getBannerPushAnimation('outgoing', shouldAnimate)}
                        isInteractive={false}
                        testId="notification-banner-content-previous"
                        motionLayer="outgoing"
                    />
                )}

                <BannerContentPage
                    key={`current-${transitionId}-${activeBanner.id}`}
                    banner={activeBanner}
                    onCtaClick={activeBanner.onCtaClick}
                    className={isCarousel ? 'absolute inset-0' : 'relative'}
                    style={getBannerPushAnimation('incoming', shouldAnimate)}
                    isInteractive={true}
                    testId="notification-banner-content-current"
                    motionLayer={shouldAnimate ? 'incoming' : undefined}
                />
            </div>

            {showCloseButton && (
                <button
                    type="button"
                    onClick={onClose}
                    className="flex size-4 shrink-0 cursor-pointer items-center justify-center text-promo-banner-close-foreground"
                    aria-label="Close banner"
                    data-testid="notification-banner-close"
                >
                    <X className="size-4 text-promo-banner-close-foreground" />
                </button>
            )}
        </div>
    );
}

/**
 * 顶部通知横幅组件。
 *
 * 视觉主题固定为 `gradient-pink`；旧调用方传入的 `variant` 仅作兼容，不会改变背景。
 */
export function NotificationBanner(props: NotificationBannerProps) {
    const {
        message,
        appearance = 'standard',
        ctaText = 'Read more',
        showCta = false,
        onCtaClick,
        showCloseButton = true,
        onClose,
        carouselItems,
        carouselIntervalMs = DEFAULT_CAROUSEL_INTERVAL_MS,
        className,
    } = props;
    const [isVisible, setIsVisible] = useState(true);
    const [isAutoPlayPaused, setIsAutoPlayPaused] = useState(false);
    const [nowMs, setNowMs] = useState(() => Date.now());
    const hasCountdown = Boolean(
        props.countdownEndTime || carouselItems?.some((item) => item.countdownEndTime)
    );

    useEffect(() => {
        if (!hasCountdown) return;

        const timer = window.setInterval(() => setNowMs(Date.now()), 1000);
        return () => window.clearInterval(timer);
    }, [hasCountdown]);

    const carouselSlides = useMemo<NotificationBannerCarouselItem[]>(() => {
        const currentBanner: NotificationBannerCarouselItem = {
            id: props.id,
            message,
            variant: NOTIFICATION_BANNER_VARIANT,
            ctaText,
            ctaLink: props.ctaLink,
            ctaLinkType: props.ctaLinkType,
            showCta,
            showCloseButton,
            countdownEndTime: props.countdownEndTime,
        };

        const configuredSlides = carouselItems
            ?.filter((item) => item.message)
            .map((item) => ({ ...item, variant: NOTIFICATION_BANNER_VARIANT }));
        const candidates =
            configuredSlides && configuredSlides.length > 0 ? configuredSlides : [currentBanner];
        return candidates.filter((item) => {
            if (!item.countdownEndTime) return true;
            const endMs = Date.parse(item.countdownEndTime);
            return !Number.isFinite(endMs) || endMs > nowMs;
        });
    }, [
        carouselItems,
        props.id,
        props.ctaLink,
        props.ctaLinkType,
        props.countdownEndTime,
        message,
        ctaText,
        showCta,
        showCloseButton,
        nowMs,
    ]);
    const { activeIndex, previousIndex, transitionId } = useBannerCarouselIndex(
        carouselSlides.length,
        carouselIntervalMs,
        isAutoPlayPaused
    );
    const handleMouseEnter = useCallback(() => {
        setIsAutoPlayPaused(true);
    }, []);
    const handleMouseLeave = useCallback(() => {
        setIsAutoPlayPaused(false);
    }, []);

    if (!isVisible || carouselSlides.length === 0) {
        return null;
    }

    const activeSlide = (carouselSlides[activeIndex] ??
        carouselSlides[0]) as NotificationBannerCarouselItem;
    const previousSlide =
        previousIndex === null ? undefined : (carouselSlides[previousIndex] ?? carouselSlides[0]);
    const createBannerProps = (slide: NotificationBannerCarouselItem): NotificationBannerProps => ({
        ...props,
        ...slide,
        appearance,
        variant: NOTIFICATION_BANNER_VARIANT,
        ctaText: slide.ctaText ?? 'Read more',
        ctaLinkType: slide.ctaLinkType ?? 'externalLink',
        showCta: slide.showCta ?? false,
        showCloseButton: slide.showCloseButton ?? true,
        carouselItems: carouselSlides,
        carouselIntervalMs,
        onCtaClick,
        onClose,
        className,
    });
    const activeBanner = createBannerProps(activeSlide);
    const previousBanner = previousSlide ? createBannerProps(previousSlide) : undefined;
    const handleClose = () => {
        setIsVisible(false);
        onClose?.(activeSlide);
    };

    return (
        <div
            className={cn(
                'relative flex h-10 w-full items-center justify-between overflow-hidden px-2',
                hasCountdown ? 'py-2' : 'py-1.5',
                className
            )}
            data-testid="notification-banner"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <BannerAnimationStyles />
            <BannerBackground />
            {appearance === 'promotion' ? <BannerForegroundNoiseFilter /> : null}
            <BannerContent
                activeBanner={activeBanner}
                previousBanner={previousBanner}
                hasCountdown={hasCountdown}
                showCloseButton={activeBanner.showCloseButton ?? true}
                onClose={handleClose}
                carouselItemCount={carouselSlides.length}
                transitionId={transitionId}
            />
        </div>
    );
}
